import React, { useState, useEffect, useCallback } from 'react';
import { userUid } from '../../../Constant/Constant';

const TowerInfoForm = ({ onDataChange, initialTowers = [], isEdit }) => {
  const [totalTowers, setTotalTowers] = useState(initialTowers.length);
  const [exitingTowerCount,setExitingTowerCount] = useState(initialTowers.length)
  const [towers, setTowers] = useState(
    initialTowers.length > 0
      ? initialTowers.map((tower, index) => ({
          id: tower.id || null,
          towerNum: index + 1,
          towerName: tower.towerName || '',
          overAllTowerArea: tower.overAllTowerArea || '',
          towerBuildUpArea: tower.towerBuildUpArea || '',
          towerTerraceArea: tower.towerTerraceArea || '',
          totalFloors: tower.totalFloors || '',
          totalBuildUpCost: tower.totalBuildUpCost || '',
          highRise: tower.highRise || [],
          createdBy: tower.createdBy || userUid,
        }))
      : [
          {
            id: null,
            towerNum: 1,
            towerName: '',
            overAllTowerArea: '',
            towerBuildUpArea: '',
            towerTerraceArea: '',
            totalFloors: '',
            totalBuildUpCost: '',
            highRise: [],
            createdBy: userUid,
          },
        ]
  );

  const [floorInputs, setFloorInputs] = useState({});

  const initializeFloorInputs = useCallback(() => {
    const initialFloorInputs = towers.reduce((acc, tower, index) => {
      if (tower.highRise.length > 0) {
        acc[index] = tower.highRise.reduce((floorAcc, range, i) => {
          const setId = `${tower.id || 'new'}-${i}`;
          floorAcc[setId] = {
            start: range.startFloor.toString(),
            end: range.endFloor.toString(),
            price: range.pricePerFloor.toString(),
          };
          return floorAcc;
        }, {});
      }
      return acc;
    }, {});
    setFloorInputs(initialFloorInputs);
  }, [towers]);

  useEffect(() => {
    initializeFloorInputs();
  }, [initializeFloorInputs]);

  useEffect(() => {
    onDataChange?.(towers);
  }, [towers, onDataChange]);

  const addNewTower = useCallback(() => {
    const newTowerNum = towers.length + 1;
    setTowers((prev) => [
      ...prev,
      {
        id: null,
        towerNum: newTowerNum,
        towerName: '',
        overAllTowerArea: '',
        towerBuildUpArea: '',
        towerTerraceArea: '',
        totalFloors: '',
        totalBuildUpCost: '',
        highRise: [],
        createdBy: userUid,
      },
    ]);
  }, [towers.length]);

  const handleTowerInputChange = useCallback((towerIndex, field, value) => {
    const fieldConfig = towerFields.find((f) => f.key === field);
    const maxLength = fieldConfig?.maxLength;
    const truncatedValue = maxLength ? value.slice(0, maxLength) : value;

    setTowers((prevTowers) =>
      prevTowers.map((tower, index) =>
        index === towerIndex
          ? { ...tower, [field]: truncatedValue }
          : tower
      )
    );
  }, []);

  const handleFloorInputChange = useCallback((towerIndex, field, value, inputSetId) => {
    const maxLengths = { start: 2, end: 2, price: 10 };
    const truncatedValue = maxLengths[field] ? value.slice(0, maxLengths[field]) : value;

    setFloorInputs((prev) => {
      const updatedFloorInputs = {
        ...prev,
        [towerIndex]: {
          ...prev[towerIndex],
          [inputSetId]: {
            ...(prev[towerIndex]?.[inputSetId] || { start: '', end: '', price: '' }),
            [field]: truncatedValue,
          },
        },
      };

      setTowers((prevTowers) =>
        prevTowers.map((tower, index) =>
          index === towerIndex
            ? {
                ...tower,
                highRise: updatedFloorInputs[towerIndex]
                  ? Object.values(updatedFloorInputs[towerIndex]).map((input) => ({
                      startFloor: parseInt(input.start) || 1,
                      endFloor: parseInt(input.end) || 1,
                      pricePerFloor: parseFloat(input.price) || 0,
                    }))
                  : tower.highRise,
              }
            : tower
        )
      );

      return updatedFloorInputs;
    });
  }, []);



  const addNewFloorInputSet = useCallback((towerIndex) => {
    const newSetId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setFloorInputs((prev) => {
      const updatedFloorInputs = {
        ...prev,
        [towerIndex]: {
          ...prev[towerIndex],
          [newSetId]: { start: '', end: '', price: '' },
        },
      };
      setTowers((prevTowers) =>
        prevTowers.map((tower, index) =>
          index === towerIndex
            ? {
                ...tower,
                highRise: updatedFloorInputs[towerIndex]
                  ? Object.values(updatedFloorInputs[towerIndex]).map((input) => ({
                      startFloor: parseInt(input.start) || 1,
                      endFloor: parseInt(input.end) || 1,
                      pricePerFloor: parseFloat(input.price) || 0,
                    }))
                  : tower.highRise,
              }
            : tower
        )
      );
      return updatedFloorInputs;
    });
  }, []);

  useEffect(() => {
    if (towers.length < totalTowers) {
      addNewTower();
    } else if (towers.length > totalTowers) {
      setTowers((prev) => prev.slice(0, totalTowers));
      setFloorInputs((prev) => {
        const newInputs = { ...prev };
        Object.keys(newInputs).forEach((key) => {
          if (parseInt(key) >= totalTowers) delete newInputs[key];
        });
        return newInputs;
      });
    }
  }, [totalTowers, addNewTower]);

  const towerFields = [
    { label: 'Tower Name', key: 'towerName', type: 'text', required: true, maxLength: 50 },
    { label: 'Overall Tower Area (sqft)', key: 'overAllTowerArea', type: 'number', required: false, maxLength: 10 },
    { label: 'Build-Up Area (sqft)', key: 'towerBuildUpArea', type: 'number', required: false, maxLength: 10 },
    { label: 'Terrace Area (sqft)', key: 'towerTerraceArea', type: 'number', required: false, maxLength: 10 },
    { label: 'Total No. of Floors', key: 'totalFloors', type: 'number', required: true, maxLength: 2 },
    { label: 'Total Build-Up Cost', key: 'totalBuildUpCost', type: 'number', required: false, maxLength: 10 },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-end ">
        <div className="flex items-center gap-3 p-3 bg-white rounded-md border border-slate-200 shadow-sm">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            <span className="inline-block w-1.5 h-4 bg-red-400 rounded-sm mr-2"></span>
            Total No. Of Towers
          </label>
          <input
            type="number"
            
            value={totalTowers  === 0 ? "" : totalTowers}
            disabled={exitingTowerCount > 0 ? true : false}
            onChange={(e) => setTotalTowers(parseInt(e.target.value))}
            className="w-40 px-3 py-2 border border-gray-300 rounded-md text-sm input-focus"
            placeholder="In Number"
          />
        </div>
      </div>

      {towers.map((tower, index) => (
        <div key={tower.towerNum} className="mb-12">
          <h2 className="text-lg font-semibold text-customGreen mb-4">
            Tower {tower.towerNum} Info
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {towerFields.map((field) => (
              <div key={field.key} className="flex items-center">
                <label className="text-sm w-[40%] font-medium text-gray-700 mb-1">
                  {field.required && (
                    <span className="border-red-400 border-l-4 mr-1 rounded-md"></span>
                  )}
                  {field.label}
                </label>
                <input
                  type={field.type}
                  min={field.type === 'number' ? 0 : undefined}
                  value={tower[field.key] || ''}
                  onChange={(e) => handleTowerInputChange(index, field.key, e.target.value)}
                  className="px-3 py-2 border w-[70%] border-gray-300 rounded-md text-sm input-focus"
                  placeholder={field.label}
                  required={field.required}
                />
              </div>
            ))}
          </div>

          {floorInputs[index] &&
            Object.keys(floorInputs[index]).map((setId) => (
              <div key={setId} className="mb-6">
                <h3 className="text-md font-semibold text-customGreen mb-4">
                  Tower {tower.towerNum} Price Per Floors
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                  <div className="flex items-center">
                    <label className="text-sm font-medium w-[40%] text-gray-700 mb-1">Start Floor</label>
                    <input
                      type="number"
                      min="0"
                      value={floorInputs[index][setId].start || ''}
                      onChange={(e) => handleFloorInputChange(index, 'start', e.target.value, setId)}
                      className="px-3 py-2 border w-[70%] border-gray-300 rounded-md text-sm input-focus"
                      placeholder="Start Floor"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="text-sm font-medium w-[40%] text-gray-700 mb-1">End Floor</label>
                    <input
                      type="number"
                      min="0"
                      value={floorInputs[index][setId].end || ''}
                      onChange={(e) => handleFloorInputChange(index, 'end', e.target.value, setId)}
                      className="px-3 py-2 border w-[70%] border-gray-300 rounded-md text-sm input-focus"
                      placeholder="End Floor"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="text-sm font-medium w-[40%] text-gray-700 mb-1">Price Per Floor</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={floorInputs[index][setId].price || ''}
                      onChange={(e) => handleFloorInputChange(index, 'price', e.target.value, setId)}
                      className="px-3 py-2 border w-[70%] border-gray-300 rounded-md text-sm input-focus"
                      placeholder="Price"
                    />
                  </div>
                </div>
              </div>
            ))}

          {exitingTowerCount === 0 && (
            <div className="flex justify-center mt-7">
              <button
                onClick={() => addNewFloorInputSet(index)}
                className="px-6 py-2 text-sm font-medium text-green-600 border border-green-600 rounded-md hover:bg-green-50 focus:outline-none"
              >
                + Add Floor Range
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default TowerInfoForm;